import { Observable } from "rxjs/internal/Observable";
import { LayoutService } from "../layout.service";
import { PageService } from "../../core/services/page.service";
import { AuthService } from "../../core/services/auth.service";
import { Router, NavigationEnd } from "@angular/router";
import { Component, HostListener } from "@angular/core";
import { filter } from "rxjs/operators";

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.css'],
    standalone: false
})
export class SidebarComponent {
  isCollapsed$: Observable<boolean>;

  notificationCount = 0;
  showNotifications = false;
  showProfileMenu = false;
  notifSubmenuOpen = false;
  activeNotifTab: 'primary' | 'promotional' = 'primary';

  // Connections submenu state
  connectionsSubmenuOpen = false;
  activeConnectionsCategory: 'personal' | 'legal' | 'others' = 'personal';

  // Tracks whether we are currently in the mobile (off-canvas drawer) layout
  private isMobileLayout = typeof window !== 'undefined' && window.innerWidth <= 768;

  constructor(
    private layoutService: LayoutService,
    private pageService: PageService,
    private authService: AuthService,
    private router: Router
  ) {
    this.isCollapsed$ = this.layoutService.sidebarCollapsed$;
  }

  ngOnInit(){
    this.authService.unreadCount$.subscribe(() => {
      // this.getNotification();
    });

    // Mobile: close the off-canvas drawer after each navigation
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => {
        this.closeSidebarOnMobile();
      });

    // Sync activeNotifTab and submenu state on every navigation
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => {
        if (this.isNotificationsActive()) {
          this.notifSubmenuOpen = true;
          const tab = this.getTabFromUrl();
          this.activeNotifTab = tab;
          localStorage.setItem('notif_tab', tab);
        } else {
          
          this.notifSubmenuOpen = false;
        }
      });

    // Also set initial state on component load
    if (this.isNotificationsActive()) {
      this.notifSubmenuOpen = true;
      this.activeNotifTab = this.getTabFromUrl();
    }

    // Sync activeConnectionsCategory and submenu state on every navigation
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => {
        if (this.isConnectionsActive()) {
          this.connectionsSubmenuOpen = true;
          const category = this.getCategoryFromUrl();
          this.activeConnectionsCategory = category;
          localStorage.setItem('connections_category', category);
        }
      });

    // Also set initial state on component load for connections
    if (this.isConnectionsActive()) {
      this.connectionsSubmenuOpen = true;
      this.activeConnectionsCategory = this.getCategoryFromUrl();
    }
  }

  private getTabFromUrl(): 'primary' | 'promotional' {
    const url = this.router.url;
    if (url.includes('tab=promotional')) return 'promotional';
    return 'primary';
  }

  private getCategoryFromUrl(): 'personal' | 'legal' | 'others' {
    const url = this.router.url;
    if (url.includes('category=legal')) return 'legal';
    if (url.includes('category=others')) return 'others';
    return 'personal';
  }
  toggleSidebar() {
    this.layoutService.toggleSidebar();
  }

  /* Close the drawer — only relevant on mobile widths */
  closeSidebarOnMobile() {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      this.layoutService.setSidebarCollapsed(true);
    }
  }

  /* Keep the sidebar state sane when the viewport crosses the mobile breakpoint */
  @HostListener('window:resize')
  onWindowResize() {
    const mobile = window.innerWidth <= 768;
    if (mobile && !this.isMobileLayout) {
      // entered mobile layout → hide the drawer
      this.layoutService.setSidebarCollapsed(true);
    } else if (!mobile && this.isMobileLayout) {
      // back to desktop layout → show the sidebar again
      this.layoutService.setSidebarCollapsed(false);
    }
    this.isMobileLayout = mobile;
  }

  onOptionClick(page: string) {
    this.pageService.setCurrentPage(page);
    localStorage.setItem('navItem', page)
    // this.router.navigate([`/${page.toLowerCase()}`]);
  }
  
  isSettingsActive(): boolean {
    const currentUrl = this.router.url;
    return currentUrl.startsWith('/consumer/settings') || currentUrl.startsWith('/consumer/profile') || currentUrl.startsWith('/consumer/country');
  }
  
  isappActive(): boolean {
    const currentUrl = this.router.url;
    return currentUrl.startsWith('/consumer/appointments');
  }
    
  iscalActive(): boolean {
    const currentUrl = this.router.url;
    return currentUrl.startsWith('/consumer/appointments');
  }

  isHealthActive(): boolean {
    const currentUrl = this.router.url;
    return currentUrl.startsWith('/consumer/health/dashbord') || currentUrl.startsWith('/consumer/health/medication') || currentUrl.startsWith('/consumer/health/profile/immunization') || 
    currentUrl.startsWith('/consumer/health/profile/insurance') || currentUrl.startsWith('/consumer/health/care-plan') || currentUrl.startsWith('/consumer/health/health-log') || currentUrl.startsWith('/consumer/health/devices') ||
    currentUrl.startsWith('/consumer/health/profile/familyhistory') || currentUrl.startsWith('/consumer/health/profile/medicalcondition') || currentUrl.startsWith('/consumer/health/profile/allergy') ||
    currentUrl.startsWith('/consumer/health/device');
  }

  onLogout() {
    // Preserve "Remember Me" data before clearing localStorage
    const rememberedEmail = localStorage.getItem('remembered_email');
    const rememberedPassword = localStorage.getItem('remembered_password');
    const rememberedChecked = localStorage.getItem('remembered_checked');

    // Clear localStorage but keep "Remember Me" credentials
    // localStorage.clear();
    // sessionStorage.clear();

    if (rememberedChecked === 'true') {
        localStorage.setItem('remembered_email', rememberedEmail || '');
        localStorage.setItem('remembered_password', rememberedPassword || '');
        localStorage.setItem('remembered_checked', rememberedChecked);
    }
    this.authService.logout()
    this.router.navigate(['/login']);
  }

  isNotificationsActive(): boolean {
    return this.router.url.startsWith('/consumer/notifications');
  }

  // Connections submenu methods
  isConnectionsActive(): boolean {
    return this.router.url.startsWith('/consumer/connections');
  }

  isConnectionsCategoryActive(category: 'personal' | 'legal' | 'others'): boolean {
    return this.isConnectionsActive() && this.activeConnectionsCategory === category;
  }

  onConnectionsParentClick(): void {
    this.pageService.setCurrentPage('Connections');
    localStorage.setItem('navItem', 'Connections');
    this.activeConnectionsCategory = 'legal';
    localStorage.setItem('connections_category', 'legal');
    this.connectionsSubmenuOpen = true;
  }

  toggleConnectionsSubmenu(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.connectionsSubmenuOpen = !this.connectionsSubmenuOpen;
  }

  onConnectionsCategoryClick(category: 'personal' | 'legal' | 'others'): void {
    this.pageService.setCurrentPage('Connections');
    localStorage.setItem('navItem', 'Connections');
    localStorage.setItem('connections_category', category);
    this.activeConnectionsCategory = category;
  }

  isNotifTabActive(tab: 'primary' | 'promotional'): boolean {
    return this.isNotificationsActive() && this.activeNotifTab === tab;
  }

  onNotifParentClick(): void {
    this.pageService.setCurrentPage('Notifications');
    localStorage.setItem('navItem', 'Notifications');
    this.activeNotifTab = 'primary';
    localStorage.setItem('notif_tab', 'primary');
    this.notifSubmenuOpen = true;
  }

  toggleNotifSubmenu(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.notifSubmenuOpen = !this.notifSubmenuOpen;
  }

  onNotifTabClick(tab: 'primary' | 'promotional'): void {
    this.pageService.setCurrentPage('Notifications');
    localStorage.setItem('navItem', 'Notifications');
    localStorage.setItem('notif_tab', tab);
    this.activeNotifTab = tab;
  }

  toggleNotifications() {
    this.showNotifications = !this.showNotifications;
    this.showProfileMenu = false;
  }

  toggleProfileMenu() {
    this.showProfileMenu = !this.showProfileMenu;
    this.showNotifications = false;
  }

  getNotification() {
    this.authService.get_notifications().subscribe((res: any) => {
      if (!res.error && res.data?.notifications?.length) {
        const allNotifications = res.data.notifications;
        // Count how many notifications have status 'unread'
        this.notificationCount = allNotifications.filter(
          (notif: any) => notif.status === 'unread'
        ).length;
      } else {
        this.notificationCount = 0;
      }
    });
  }

  navigateToHome(){
    this.router.navigate(['/consumer'])
  }
}